
import React, { useMemo, useState, useEffect } from 'react';
import { Bucket, BucketTheme, Todo } from '../types';
import { useLanguage } from '../i18n';
import { Calendar, CheckCircle2, Circle, Clock, ArrowUpDown, ArrowDown, ArrowUp, Pause, Play, Trash2, CalendarClock, CalendarDays, Hourglass, Zap, Plus, Target, CheckSquare, ListTodo } from 'lucide-react';
import { TaskItem } from './TaskItem';

interface AllTasksViewProps {
  buckets: Bucket[];
  onUpdateBucket: (id: string, updates: Partial<Bucket>) => void;
  onToggleActiveTask: (bucketId: string, todoId: string) => void;
  onJumpAndEdit: (bucketId: string, todoId: string) => void;
  onManageTask: (bucketId: string, todo?: Todo) => void;
  onCreateBucket?: () => void;
}

type SortField = 'date' | 'points' | 'bucket';
type SortDirection = 'asc' | 'desc';
type ViewFilter = 'open' | 'done';

export const AllTasksView: React.FC<AllTasksViewProps> = ({ buckets, onUpdateBucket, onToggleActiveTask, onJumpAndEdit, onManageTask, onCreateBucket }) => {
    const { t, language } = useLanguage();
    const [sortField, setSortField] = useState<SortField>('date');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [viewFilter, setViewFilter] = useState<ViewFilter>('open');
    const [currentTick, setCurrentTick] = useState(Date.now());

    const hasActiveTask = useMemo(() => buckets.some(b => b.todos.some(t => !!t.activeSessionStart)), [buckets]);
    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;
        if (hasActiveTask) interval = setInterval(() => setCurrentTick(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [hasActiveTask]);

    const filteredTodos = useMemo(() => {
        const list: { todo: Todo, bucketId: string, bucketName: string, bucketTheme: BucketTheme }[] = [];
        buckets.forEach(bucket => {
            bucket.todos.forEach(todo => {
                if (todo.completed === (viewFilter === 'done')) {
                    list.push({ todo, bucketId: bucket.id, bucketName: bucket.name, bucketTheme: bucket.theme });
                }
            });
        });
        return list;
    }, [buckets, viewFilter]);

    const sortedTodos = useMemo(() => {
        return [...filteredTodos].sort((a, b) => {
            let res = 0;
            switch (sortField) {
                case 'date':
                    // If viewing done tasks, date sort usually means completion date
                    if (viewFilter === 'done') {
                        const dateA = a.todo.completedAt || 0;
                        const dateB = b.todo.completedAt || 0;
                        res = dateA - dateB;
                    } else {
                        const dateA = a.todo.scheduledDate || Number.MAX_SAFE_INTEGER;
                        const dateB = b.todo.scheduledDate || Number.MAX_SAFE_INTEGER;
                        res = dateA - dateB;
                    }
                    break;
                case 'points':
                    res = a.todo.points - b.todo.points;
                    break;
                case 'bucket':
                    res = a.bucketName.localeCompare(b.bucketName);
                    break;
            }
            // By default, if viewing done tasks, show newest first (desc)
            const finalDirection = (sortField === 'date' && viewFilter === 'done' && sortDirection === 'asc') ? 'desc' : sortDirection;
            return sortDirection === 'asc' ? res : -res;
        });
    }, [filteredTodos, sortField, sortDirection, viewFilter]);

    const handleSort = (field: SortField) => {
        if (sortField === field) setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDirection('asc'); }
    };

    const handleToggleTodo = (bucketId: string, todoId: string) => {
        const bucket = buckets.find(b => b.id === bucketId);
        if(!bucket) return;
        const todo = bucket.todos.find(t => t.id === todoId);
        if(!todo) return;
        const isCompleting = !todo.completed;
        let updatedTodo = { ...todo };
        if (todo.recurrence && isCompleting) {
             const now = new Date(); let nextDate = new Date();
             if (todo.recurrence === 'daily') nextDate.setDate(now.getDate() + 1);
             else if (todo.recurrence === 'weekly') nextDate.setDate(now.getDate() + (8 - now.getDay()) % 7 || 7);
             else if (todo.recurrence === 'monthly') nextDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
             nextDate.setHours(0,0,0,0);
             updatedTodo = { ...updatedTodo, completed: false, accumulatedPoints: (todo.accumulatedPoints || 0) + todo.points, snoozedUntil: nextDate.getTime(), activeSessionStart: undefined };
        } else {
             updatedTodo = { ...updatedTodo, completed: isCompleting, completedAt: isCompleting ? Date.now() : undefined, activeSessionStart: undefined }
        }
        const updatedTodos = bucket.todos.map(t => t.id === todoId ? updatedTodo : t);
        onUpdateBucket(bucketId, { todos: updatedTodos });
    };

    const handleDelete = (bucketId: string, todoId: string) => {
        if(!confirm("Delete this task?")) return;
        const bucket = buckets.find(b => b.id === bucketId);
        if(!bucket) return;
        onUpdateBucket(bucketId, { todos: bucket.todos.filter(t => t.id !== todoId) });
    }

    const SortButton = ({ field, label, icon: Icon }: { field: SortField, label: string, icon: any }) => (
        <button onClick={() => handleSort(field)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${sortField === field ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white/5 text-slate-400 hover:text-white hover:bg-white/10'}`}>
            <Icon size={16} /><span>{label}</span>{sortField === field && (sortDirection === 'asc' ? <ArrowUp size={14} className="ml-1" /> : <ArrowDown size={14} className="ml-1" />)}
        </button>
    );

    if (buckets.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-24 text-center animate-in fade-in duration-700">
                <div className="w-24 h-24 bg-indigo-500/10 rounded-full flex items-center justify-center mb-6 relative">
                     <div className="absolute inset-0 bg-indigo-500/20 blur-2xl rounded-full"></div>
                     <Target size={48} className="text-indigo-400 relative z-10" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-3">{t.allTasksView}</h2>
                <p className="text-slate-500 max-w-md mx-auto mb-8">{t.introSubtitle}</p>
                {onCreateBucket && (
                    <button onClick={onCreateBucket} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all shadow-xl shadow-indigo-600/20 active:scale-95"><Plus size={20} />{t.createFirstBucket}</button>
                )}
            </div>
        );
    }

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 h-full flex flex-col max-w-5xl mx-auto">
             <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 shrink-0">
                <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                        {viewFilter === 'open' ? <CheckCircle2 className="text-indigo-400" /> : <CheckSquare className="text-emerald-400" />}
                        {t.allTasksView}
                    </h2>
                    <div className="flex bg-slate-900/50 p-1 rounded-xl border border-white/10">
                        <button 
                            onClick={() => setViewFilter('open')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${viewFilter === 'open' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
                        >
                            <ListTodo size={14} />
                            {t.openTasks}
                        </button>
                        <button 
                            onClick={() => setViewFilter('done')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-2 ${viewFilter === 'done' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}
                        >
                            <CheckSquare size={14} />
                            {t.doneTasks}
                        </button>
                    </div>
                </div>
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 bg-slate-900/50 p-1.5 rounded-xl border border-white/10">
                    <span className="text-xs text-slate-500 font-bold uppercase tracking-wider px-2">{t.sortBy}</span>
                    <SortButton field="date" label={viewFilter === 'done' ? t.done : t.sortDate} icon={Calendar} />
                    <SortButton field="points" label={t.sortPoints} icon={Zap} />
                    <SortButton field="bucket" label={t.sortBucket} icon={Circle} />
                </div>
             </div>
             <div className="space-y-2">
                 {sortedTodos.length === 0 ? (
                    <div className="text-center py-20 text-slate-500">
                        <p>{viewFilter === 'open' ? 'No open tasks found. Great job!' : 'No completed tasks yet. Pour something in!'}</p>
                    </div>
                 ) : (
                    sortedTodos.map(item => (
                        <TaskItem 
                            key={item.todo.id} 
                            todo={item.todo} 
                            bucketId={item.bucketId} 
                            bucketName={item.bucketName} 
                            bucketTheme={item.bucketTheme} 
                            isActive={!!item.todo.activeSessionStart} 
                            currentTick={currentTick} 
                            onToggle={() => handleToggleTodo(item.bucketId, item.todo.id)} 
                            onToggleTimer={() => onToggleActiveTask(item.bucketId, item.todo.id)} 
                            onEdit={() => onManageTask(item.bucketId, item.todo)} 
                            onDelete={() => handleDelete(item.bucketId, item.todo.id)} 
                            showBucketName={true} 
                        />
                    ))
                 )}
             </div>
        </div>
    );
};
